import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
    ArrowLeft,
    ArrowUpRight,
    MapPin,
    Calendar,
    Tag,
    CheckCircle2,
    PoundSterling,
} from "lucide-react";
import Reveal from "@/components/common/Reveal";
import PageSEO from "@/components/seo/PageSEO";
import { projectImage, absoluteUrl } from "@/components/seo/schemaHelpers";
import { fetchProject } from "@/lib/api";

function formatValueGBP(v) {
    if (!v || typeof v !== "number") return null;
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
    return `£${v}`;
}

export default function ProjectDetailPage() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchProject(slug)
            .then(setProject)
            .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "unknown"))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div
                className="container-x py-40 font-mono-tag text-brand-steel"
                data-testid="project-loading"
            >
                LOADING PROJECT…
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <div className="container-x py-40" data-testid="project-not-found">
                <p className="font-mono-tag text-brand-gold">[ 404 ]</p>
                <h1 className="mt-6 font-display text-6xl font-semibold leading-[1.05]">
                    Project not found
                </h1>
                <Link
                    to="/projects"
                    className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal hover:text-brand-gold"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to projects
                </Link>
            </div>
        );
    }

    if (!project) return null;

    const cover = project.cover_image || project.image;
    const capabilities = project.capabilities || [];
    const priceLabel = formatValueGBP(project.value_gbp);
    const contactHref = `/contact?service_of_interest=${encodeURIComponent(
        project.category || "",
    )}&subject=${encodeURIComponent(`Similar project to ${project.title}`)}`;

    return (
        <div className="bg-brand-cream" data-testid="project-detail-page">
            <PageSEO
                title={`${project.title} — ${project.category} Project in ${project.location} | EB Construction`}
                description={(project.summary || "").slice(0, 155)}
                canonicalPath={`/projects/${project.slug}`}
                ogImage={projectImage(project)}
                ogType="article"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: project.title,
                    description: project.summary,
                    image: projectImage(project),
                    about: project.category,
                    locationCreated: {
                        "@type": "Place",
                        name: project.location,
                    },
                    dateCreated: project.year ? `${project.year}-01-01` : undefined,
                    creator: {
                        "@id": "https://elevatebuildconstruction.co.uk/#organization",
                    },
                    publisher: {
                        "@id": "https://elevatebuildconstruction.co.uk/#organization",
                    },
                    mainEntityOfPage: absoluteUrl(`/projects/${project.slug}`),
                    ...(project.value_gbp
                        ? {
                              offers: {
                                  "@type": "Offer",
                                  price: project.value_gbp,
                                  priceCurrency: "GBP",
                              },
                          }
                        : {}),
                }}
            />
            <section className="border-b border-brand-line">
                <div className="container-x pb-6 pt-10">
                    <Link
                        to="/projects"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-steel hover:text-brand-gold"
                        data-testid="back-to-projects"
                    >
                        <ArrowLeft className="h-3 w-3" /> All Projects
                    </Link>
                </div>
                <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                        src={cover}
                        alt={project.title}
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/40 to-transparent" />
                    <div className="container-x absolute inset-x-0 bottom-0 pb-12 text-brand-offwhite">
                        <Reveal>
                            <p className="font-mono-tag text-brand-gold">
                                [ {project.category} · {project.year} ]
                            </p>
                            <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] md:text-6xl lg:text-7xl">
                                {project.title}
                            </h1>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-line py-20 md:py-24">
                <div className="container-x grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-4">
                        <Reveal>
                            <div className="flex flex-col gap-6 border-t border-brand-line pt-6">
                                <div className="flex items-start gap-3">
                                    <Tag className="mt-1 h-4 w-4 text-brand-gold" />
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Category
                                        </p>
                                        <p className="font-display text-2xl font-semibold leading-tight">
                                            {project.category}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <MapPin className="mt-1 h-4 w-4 text-brand-gold" />
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Location
                                        </p>
                                        <p className="font-display text-2xl font-semibold leading-tight">
                                            {project.location}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Calendar className="mt-1 h-4 w-4 text-brand-gold" />
                                    <div>
                                        <p className="font-mono-tag text-brand-steel">
                                            Completed
                                        </p>
                                        <p className="font-display text-2xl font-semibold leading-tight">
                                            {project.year}
                                        </p>
                                    </div>
                                </div>
                                {priceLabel && (
                                    <div
                                        className="flex items-start gap-3"
                                        data-testid="project-detail-value"
                                    >
                                        <PoundSterling className="mt-1 h-4 w-4 text-brand-gold" />
                                        <div>
                                            <p className="font-mono-tag text-brand-steel">
                                                Contract value
                                            </p>
                                            <p className="font-industrial text-3xl text-brand-gold leading-none">
                                                {priceLabel}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7 md:col-start-6">
                        <Reveal delay={0.1}>
                            {project.summary && (
                                <p className="accent-italic text-2xl leading-snug text-brand-charcoal">
                                    {project.summary}
                                </p>
                            )}
                            <div
                                className="prose prose-neutral mt-6 max-w-[68ch] font-body text-base leading-relaxed text-brand-steel [&_p]:mt-4 [&_strong]:text-brand-charcoal"
                                data-testid="project-detail-body"
                            >
                                {project.description_md ? (
                                    <ReactMarkdown>{project.description_md}</ReactMarkdown>
                                ) : (
                                    <p>{project.description}</p>
                                )}
                            </div>
                        </Reveal>

                        {capabilities.length > 0 && (
                            <Reveal delay={0.15}>
                                <div className="mt-10 border-t border-brand-line pt-8">
                                    <p className="font-mono-tag text-brand-gold">
                                        Capabilities delivered
                                    </p>
                                    <ul
                                        className="mt-4 space-y-2"
                                        data-testid="project-detail-capabilities"
                                    >
                                        {capabilities.map((c) => (
                                            <li
                                                key={c}
                                                className="flex items-start gap-3 font-body text-sm text-brand-charcoal"
                                            >
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                                <span>{c}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            <section className="bg-brand-charcoal py-20 text-brand-offwhite md:py-28">
                <div className="container-x flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                    <div>
                        <p className="font-mono-tag text-brand-gold">
                            [ Build with us ]
                        </p>
                        <h3 className="mt-4 max-w-2xl font-display text-3xl font-semibold leading-[1.1] md:text-5xl">
                            Enquire about a similar project.
                        </h3>
                    </div>
                    <Link
                        to={contactHref}
                        className="btn-primary"
                        data-testid="project-cta-contact"
                    >
                        Request a quote
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
